import { useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useUiStore } from "../../store/uiStore";
import { useAuthStore } from "../../store/authStore";
import { useReorderAlertStore } from "../../store/reorderAlertStore";
import { useOrderStore } from "../../store/orderStore";
import { AlertBadge } from "../molecules/AlertBadge";
import { OrdersBadge } from "../molecules/OrdersBadge";
import { getVisibleNavItems } from "../../config/navigation";

export function Sidebar() {
  const { t } = useTranslation("common");
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const { user } = useAuthStore();
  const fetchAlertCount = useReorderAlertStore((s) => s.fetchAlertCount);
  const alertCountError = useReorderAlertStore((s) => s.alertCountError);
  const fetchToValidatePortalCount = useOrderStore((s) => s.fetchToValidatePortalCount);
  const visibleNavItems = useMemo(
    () => getVisibleNavItems(user?.role),
    [user?.role]
  );
  // Fetch alert count only for roles that can see Alerts (RBAC: admin, manager, staff)
  const canSeeAlerts = visibleNavItems.some((item) => item.path === "/alerts");
  const canSeeOrders = visibleNavItems.some((item) => item.path === "/orders");
  useEffect(() => {
    if (canSeeAlerts) fetchAlertCount();
  }, [canSeeAlerts, fetchAlertCount]);
  useEffect(() => {
    if (canSeeOrders) fetchToValidatePortalCount();
  }, [canSeeOrders, fetchToValidatePortalCount]);

  return (
    <aside
      className={`hidden md:flex flex-col bg-white dark:bg-neutral-800 border-e border-neutral-200 dark:border-neutral-700 transition-all duration-300 ${
        sidebarCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/* Navigation Items */}
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors min-h-touch focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800 ${
                    isActive
                      ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400"
                      : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                  }`
                }
                title={
                  item.path === "/alerts" && alertCountError
                    ? t("alerts_error_title", { error: alertCountError })
                    : sidebarCollapsed
                      ? t(item.labelKey)
                      : undefined
                }
              >
                <span className="relative flex items-center">
                  <item.icon className="w-6 h-6 flex-shrink-0" />
                  {item.path === "/alerts" && sidebarCollapsed && (
                    <span className="absolute -top-1 -end-1">
                      <AlertBadge />
                    </span>
                  )}
                  {item.path === "/orders" && sidebarCollapsed && (
                    <span className="absolute -top-1 -end-1">
                      <OrdersBadge />
                    </span>
                  )}
                </span>
                {!sidebarCollapsed && (
                  <span className="flex items-center gap-2 font-medium truncate">
                    {t(item.labelKey)}
                    {item.path === "/alerts" && <AlertBadge />}
                    {item.path === "/orders" && <OrdersBadge />}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Collapse Toggle Button */}
      <div className="p-2 border-t border-neutral-200 dark:border-neutral-700">
        <button
          onClick={toggleSidebar}
          className="flex items-center justify-center w-full p-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800"
          aria-label={sidebarCollapsed ? t("expand_sidebar") : t("collapse_sidebar")}
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon className="w-5 h-5 rtl-flip" />
          ) : (
            <>
              <ChevronLeftIcon className="w-5 h-5 me-2 rtl-flip" />
              <span className="text-sm">{t("collapse")}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
